if (!customElements.get('vl-pdp-rider-stories')) {
  class VLPDPRiderStories extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.isInitialized = false;
      this.abortController = null;
      this.instantFrame = null;
      this.pointerState = null;
      this.suppressClick = false;
      this.transitionToken = 0;
      this.isSettling = false;
      this.resizeObserver = null;

      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleNavigationClick = this.handleNavigationClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerEnd = this.handlePointerEnd.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.slides = [...this.querySelectorAll('[data-vl-pdp-rider-stories-slide]')];
      this.backgrounds = [...this.querySelectorAll('[data-vl-pdp-rider-stories-background]')];
      this.navigationItems = [
        ...this.querySelectorAll('[data-vl-pdp-rider-stories-navigation-item]'),
      ];
      this.cardViewport = this.querySelector('[data-vl-pdp-rider-stories-gesture]');
      this.navigationViewport = this.querySelector(
        '[data-vl-pdp-rider-stories-navigation-viewport]',
      );
      this.navigation = this.querySelector('[data-vl-pdp-rider-stories-navigation]');
      this.inner = this.querySelector('.vl-pdp-rider-stories__inner');
      this.previousButton = this.querySelector('[data-vl-pdp-rider-stories-previous]');
      this.nextButton = this.querySelector('[data-vl-pdp-rider-stories-next]');
      this.status = this.querySelector('[data-vl-pdp-rider-stories-status]');

      if (!this.cardViewport || this.slides.length === 0) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', this.handlePreviousClick, { signal });
      this.nextButton?.addEventListener('click', this.handleNextClick, { signal });
      this.addEventListener('click', this.handleNavigationClick, { signal });
      this.cardViewport.addEventListener('keydown', this.handleKeydown, { signal });

      this.querySelectorAll('[data-vl-pdp-rider-stories-gesture]').forEach((target) => {
        target.addEventListener('pointerdown', this.handlePointerDown, { signal });
        target.addEventListener('pointermove', this.handlePointerMove, { signal });
        target.addEventListener('pointerup', this.handlePointerEnd, { signal });
        target.addEventListener('pointercancel', this.handlePointerEnd, { signal });
      });

      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

      if ('ResizeObserver' in window && this.navigationViewport) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.navigationViewport);
      } else {
        window.addEventListener('resize', this.handleResize, { signal });
      }

      const initialIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.commitSlide(initialIndex >= 0 ? initialIndex : 0, false, false);
      this.preloadAdjacent(this.currentIndex);
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.instantFrame !== null) cancelAnimationFrame(this.instantFrame);

      this.navigation?.style.removeProperty('transition');
      this.navigation?.style.removeProperty('transform');
      this.clearCardDragStyles();

      this.abortController = null;
      this.resizeObserver = null;
      this.instantFrame = null;
      this.pointerState = null;
      this.isInitialized = false;
      this.transitionToken += 1;
    }

    handlePreviousClick() {
      if (this.isSettling) return;
      this.showSlide(this.currentIndex - 1);
    }

    handleNextClick() {
      if (this.isSettling) return;
      this.showSlide(this.currentIndex + 1);
    }

    handleNavigationClick(event) {
      const item = event.target.closest('[data-vl-pdp-rider-stories-navigation-item]');
      if (!item || !this.contains(item)) return;

      if (this.suppressClick) {
        event.preventDefault();
        this.suppressClick = false;
        return;
      }

      this.showSlide(Number(item.dataset.slideIndex));
    }

    handleKeydown(event) {
      if (event.target !== this.cardViewport) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePreviousClick();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNextClick();
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.showSlide(0);
      }

      if (event.key === 'End') {
        event.preventDefault();
        this.showSlide(this.slides.length - 1);
      }
    }

    handlePointerDown(event) {
      if (this.slides.length < 2) return;
      if (this.isSettling) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.currentTarget === this.cardViewport && event.target.closest('button')) return;

      this.pointerState = {
        target: event.currentTarget,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        currentX: event.clientX,
        currentY: event.clientY,
        axis: null,
        isCard: event.currentTarget === this.cardViewport,
        isNavigation: event.currentTarget === this.navigationViewport,
        navigationDrag: null,
        hasCapture: event.currentTarget === this.cardViewport,
      };

      if (this.pointerState.hasCapture) {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
    }

    handlePointerMove(event) {
      if (!this.pointerState || event.pointerId !== this.pointerState.pointerId) return;

      this.pointerState.currentX = event.clientX;
      this.pointerState.currentY = event.clientY;

      const deltaX = event.clientX - this.pointerState.startX;
      const deltaY = event.clientY - this.pointerState.startY;

      if (!this.pointerState.axis && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
        this.pointerState.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      }

      if (this.pointerState.axis !== 'x') return;

      event.preventDefault();
      this.classList.add('is-dragging');

      if (!this.pointerState.hasCapture) {
        this.pointerState.target.setPointerCapture?.(event.pointerId);
        this.pointerState.hasCapture = true;
      }

      if (this.pointerState.isCard) {
        this.updateCardDrag(deltaX);
      }

      if (this.pointerState.isNavigation && this.navigation) {
        this.updateNavigationDrag(deltaX);
      }
    }

    async handlePointerEnd(event) {
      if (!this.pointerState || event.pointerId !== this.pointerState.pointerId) return;

      const pointerState = this.pointerState;
      const endX = Number.isFinite(event.clientX) ? event.clientX : this.pointerState.currentX;
      const endY = Number.isFinite(event.clientY) ? event.clientY : this.pointerState.currentY;
      const deltaX = endX - this.pointerState.startX;
      const deltaY = endY - this.pointerState.startY;
      const width = this.pointerState.target.clientWidth || this.cardViewport.clientWidth;
      const threshold = Math.min(72, Math.max(40, width * 0.12));
      const movedHorizontally = Math.abs(deltaX) >= threshold && Math.abs(deltaX) > Math.abs(deltaY);
      const direction = deltaX < 0 ? 1 : -1;

      if (pointerState.axis === 'x' && Math.abs(deltaX) > 8) {
        this.suppressClick = true;
      }

      if (pointerState.target.hasPointerCapture?.(event.pointerId)) {
        pointerState.target.releasePointerCapture(event.pointerId);
      }

      this.pointerState = null;

      if (pointerState.isCard && pointerState.axis === 'x') {
        await this.settleCardDrag(
          event.type !== 'pointercancel' && movedHorizontally ? direction : 0,
          true,
        );
      } else if (pointerState.isNavigation && pointerState.axis === 'x') {
        await this.settleNavigationCarouselDrag(
          pointerState.navigationDrag,
          event.type !== 'pointercancel' && movedHorizontally,
        );
      } else {
        this.classList.remove('is-dragging');
      }

      if (this.suppressClick) {
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 250);
      }
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index === -1) return;

      this.showSlide(index, false, false);
    }

    async showSlide(index, animate = true, announce = true) {
      const slideCount = this.slides.length;
      if (slideCount === 0) return;
      if (this.isSettling) return;

      const targetIndex = Math.max(0, Math.min(index, slideCount - 1));
      if (targetIndex === this.currentIndex) return;
      const token = ++this.transitionToken;

      await this.hydrateBackground(targetIndex);
      if (token !== this.transitionToken || !this.isConnected) return;

      const shouldAnimate = animate && !this.prefersReducedMotion;

      if (shouldAnimate) {
        this.isSettling = true;
        this.classList.add('is-navigation-settling');
        await this.animateNavigationTo(targetIndex);
        this.classList.remove('is-navigation-settling');
        this.isSettling = false;
      } else {
        this.orderNavigationImmediately(targetIndex);
      }

      if (token !== this.transitionToken || !this.isConnected) return;

      this.commitSlide(targetIndex, animate, announce, false);
      this.preloadAdjacent(targetIndex);
    }

    commitSlide(targetIndex, animate = true, announce = true, orderNavigation = true) {
      const shouldAnimate = animate && !this.prefersReducedMotion;

      if (!shouldAnimate) {
        this.classList.add('is-instant');
        if (this.instantFrame !== null) cancelAnimationFrame(this.instantFrame);
      }

      this.currentIndex = targetIndex;
      this.updateBackgroundRatio(targetIndex);

      this.backgrounds.forEach((background, index) => {
        background.classList.toggle('is-active', index === targetIndex);
      });

      this.slides.forEach((slide, index) => {
        const isActive = index === targetIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.inert = !isActive;
      });

      this.setNavigationActive(targetIndex);

      if (this.previousButton) this.previousButton.disabled = targetIndex === 0;
      if (this.nextButton) this.nextButton.disabled = targetIndex === this.slides.length - 1;

      if (orderNavigation) {
        this.orderNavigationImmediately(targetIndex);
      }

      if (announce && this.status) {
        this.status.textContent = `Showing rider story ${targetIndex + 1} of ${this.slides.length}`;
      }

      if (!shouldAnimate) {
        this.instantFrame = requestAnimationFrame(() => {
          this.classList.remove('is-instant');
          this.instantFrame = null;
        });
      }
    }

    updateBackgroundRatio(index) {
      const background = this.backgrounds[index];
      if (!background) return;

      const desktopRatio = Number.parseFloat(background.dataset.backgroundRatio);
      const mobileRatio = Number.parseFloat(background.dataset.backgroundRatioMobile);
      const desktopHeight = Number.parseFloat(background.dataset.backgroundDesktopHeight);

      if (Number.isFinite(desktopRatio) && desktopRatio > 0) {
        this.style.setProperty('--vl-pdp-rider-stories-background-ratio', desktopRatio);
      }

      if (Number.isFinite(mobileRatio) && mobileRatio > 0) {
        this.style.setProperty('--vl-pdp-rider-stories-background-ratio-mobile', mobileRatio);
      }

      if (Number.isFinite(desktopHeight) && desktopHeight > 0) {
        this.style.setProperty('--vl-pdp-rider-stories-desktop-height', desktopHeight);
      }
    }

    hydrateBackground(index) {
      const background = this.backgrounds[index];
      const image = background?.querySelector('[data-vl-pdp-rider-stories-background-image]');

      if (!image || image.dataset.loaded === 'true') return Promise.resolve();
      if (image.dataset.loading === 'true') {
        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      }

      image.dataset.loading = 'true';
      const picture = image.closest('picture');

      picture?.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });

      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        image.removeAttribute('data-srcset');
      }

      if (image.dataset.src) {
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      }

      return new Promise((resolve) => {
        const finish = () => {
          image.dataset.loaded = 'true';
          delete image.dataset.loading;
          resolve();
        };

        if (image.complete && image.naturalWidth > 1) {
          finish();
        } else {
          image.addEventListener('load', finish, { once: true });
          image.addEventListener('error', finish, { once: true });
        }
      });
    }

    preloadAdjacent(index) {
      if (this.slides.length < 2) return;

      const run = () => {
        if (index > 0) this.hydrateBackground(index - 1);
        if (index < this.slides.length - 1) this.hydrateBackground(index + 1);
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1200 });
      } else {
        window.setTimeout(run, 200);
      }
    }

    updateCardDrag(deltaX) {
      const width = this.cardViewport.clientWidth;
      const activeSlide = this.slides[this.currentIndex];
      const previousSlide = this.currentIndex > 0 ? this.slides[this.currentIndex - 1] : null;
      const nextSlide =
        this.currentIndex < this.slides.length - 1 ? this.slides[this.currentIndex + 1] : null;
      const isPastStart = this.currentIndex === 0 && deltaX > 0;
      const isPastEnd = this.currentIndex === this.slides.length - 1 && deltaX < 0;
      const adjustedDelta = isPastStart || isPastEnd ? deltaX * 0.28 : deltaX;

      previousSlide?.classList.add('is-drag-preview');
      nextSlide?.classList.add('is-drag-preview');
      activeSlide.style.transform = `translate3d(${adjustedDelta}px, 0, 0)`;

      if (previousSlide) {
        previousSlide.style.transform = `translate3d(${adjustedDelta - width}px, 0, 0)`;
      }

      if (nextSlide) {
        nextSlide.style.transform = `translate3d(${adjustedDelta + width}px, 0, 0)`;
      }
    }

    async settleCardDrag(direction, announce) {
      const width = this.cardViewport.clientWidth;
      const activeSlide = this.slides[this.currentIndex];
      const targetIndex = Math.max(
        0,
        Math.min(this.currentIndex + direction, this.slides.length - 1),
      );
      const effectiveDirection = targetIndex - this.currentIndex;
      const targetSlide = this.slides[targetIndex];
      const duration = this.prefersReducedMotion ? 0 : 360;

      this.isSettling = true;
      this.classList.remove('is-dragging');
      this.classList.add('is-settling');

      if (effectiveDirection !== 0) {
        await this.hydrateBackground(targetIndex);
      }

      activeSlide.style.transitionDuration = `${duration}ms`;
      targetSlide.style.transitionDuration = `${duration}ms`;

      requestAnimationFrame(() => {
        activeSlide.style.transform =
          effectiveDirection === 0
            ? 'translate3d(0, 0, 0)'
            : `translate3d(${-effectiveDirection * width}px, 0, 0)`;

        if (effectiveDirection !== 0) {
          targetSlide.style.transform = 'translate3d(0, 0, 0)';
        }
      });

      const navigationAnimation =
        effectiveDirection === 0
          ? Promise.resolve()
          : this.animateNavigationTo(targetIndex);

      await Promise.all([this.wait(duration), navigationAnimation]);

      if (effectiveDirection !== 0) {
        this.commitSlide(targetIndex, false, announce, false);
        this.preloadAdjacent(targetIndex);
      }

      this.clearCardDragStyles();
      this.classList.remove('is-settling');
      this.isSettling = false;
    }

    clearCardDragStyles() {
      this.slides.forEach((slide) => {
        slide.classList.remove('is-drag-preview');
        slide.style.removeProperty('transform');
        slide.style.removeProperty('transition-duration');
      });
    }

    updateNavigationDrag(deltaX) {
      if (!this.pointerState?.navigationDrag) {
        this.pointerState.navigationDrag = this.prepareNavigationDrag();
      }

      const dragState = this.pointerState.navigationDrag;
      if (!dragState) return;

      const requestedOffset = dragState.baseOffset - deltaX;
      dragState.requestedOffset = requestedOffset;
      dragState.currentOffset = Math.max(
        0,
        Math.min(requestedOffset, this.getNavigationMaxOffset()),
      );

      this.setNavigationOffset(dragState.currentOffset);
    }

    prepareNavigationDrag() {
      if (!this.navigation || this.navigationItems.length < 2) return null;

      const baseOffset = this.getNavigationTargetOffset(this.currentIndex);

      this.navigation.style.transition = 'none';

      return {
        baseOffset,
        currentOffset: baseOffset,
        requestedOffset: baseOffset,
      };
    }

    async settleNavigationCarouselDrag(dragState, shouldCommit) {
      if (!dragState) {
        this.classList.remove('is-dragging');
        return;
      }

      const deltaOffset = dragState.requestedOffset - dragState.baseOffset;
      const direction = deltaOffset > 0 ? 1 : -1;
      const targetIndex = shouldCommit
        ? Math.max(0, Math.min(this.currentIndex + direction, this.slides.length - 1))
        : this.currentIndex;
      const targetOffset = this.getNavigationTargetOffset(targetIndex);
      const duration =
        this.prefersReducedMotion || Math.abs(targetOffset - dragState.currentOffset) < 0.5
          ? 0
          : 300;

      this.isSettling = true;
      this.classList.remove('is-dragging');
      this.classList.add('is-navigation-settling');

      const shouldChange = targetIndex !== this.currentIndex;
      const hydration = shouldChange ? this.hydrateBackground(targetIndex) : Promise.resolve();
      await Promise.all([
        hydration,
        this.animateNavigationTransform(
          -dragState.currentOffset,
          -targetOffset,
          duration,
        ),
      ]);

      this.navigation.style.removeProperty('transition');

      if (shouldChange) {
        this.commitSlide(targetIndex, true, true, false);
        this.preloadAdjacent(targetIndex);
      }

      this.classList.remove('is-navigation-settling');
      this.isSettling = false;
    }

    orderNavigationImmediately(index) {
      if (!this.navigation || !this.navigationViewport || this.navigationItems.length === 0) {
        return;
      }

      this.navigation.style.transition = 'none';
      this.setNavigationOffset(this.getNavigationTargetOffset(index));
      this.navigation.getBoundingClientRect();
      this.navigation.style.removeProperty('transition');
    }

    async animateNavigationTo(targetIndex) {
      if (!this.navigation || !this.navigationViewport || this.navigationItems.length < 2) {
        return;
      }

      const fromOffset = this.getNavigationTargetOffset(this.currentIndex);
      const targetOffset = this.getNavigationTargetOffset(targetIndex);
      if (Math.abs(targetOffset - fromOffset) < 0.5) {
        this.setNavigationOffset(targetOffset);
        return;
      }

      const steps = Math.abs(targetIndex - this.currentIndex);
      const duration = this.prefersReducedMotion ? 0 : Math.min(600, 360 + (steps - 1) * 110);

      await this.animateNavigationTransform(-fromOffset, -targetOffset, duration);
      this.navigation.style.removeProperty('transition');
    }

    animateNavigationTransform(from, to, duration) {
      if (duration === 0 || Math.abs(to - from) < 0.5) {
        this.navigation.style.transition = 'none';
        this.navigation.style.transform = `translate3d(${to}px, 0, 0)`;
        return Promise.resolve();
      }

      this.navigation.style.transition = 'none';
      this.navigation.style.transform = `translate3d(${from}px, 0, 0)`;
      this.navigation.getBoundingClientRect();

      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          this.navigation.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
          this.navigation.style.transform = `translate3d(${to}px, 0, 0)`;
          window.setTimeout(resolve, duration);
        });
      });
    }

    getNavigationTargetOffset(index) {
      const item = this.navigationItems[index];
      if (!item || !this.navigation) return 0;

      const itemWidth = Number.parseFloat(getComputedStyle(item).width) || item.offsetWidth;
      const gap = Number.parseFloat(getComputedStyle(this.navigation).columnGap) || 0;
      const itemOffset = index * (itemWidth + gap);

      return Math.min(itemOffset, this.getNavigationMaxOffset());
    }

    getNavigationMaxOffset() {
      if (!this.navigation || !this.navigationViewport) return 0;

      const contentWidth =
        this.inner?.getBoundingClientRect().width ||
        this.navigationViewport.getBoundingClientRect().width;

      return Math.max(0, this.navigation.scrollWidth - contentWidth);
    }

    setNavigationOffset(offset) {
      this.navigation.style.transform = `translate3d(${-offset}px, 0, 0)`;
    }

    setNavigationActive(targetIndex) {
      this.navigationItems.forEach((item, index) => {
        const isActive = index === targetIndex;
        item.classList.toggle('is-active', isActive);

        if (isActive) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      });
    }

    handleResize() {
      if (this.isSettling || this.pointerState) return;
      this.orderNavigationImmediately(this.currentIndex);
    }

    wait(duration) {
      if (duration === 0) return Promise.resolve();
      return new Promise((resolve) => window.setTimeout(resolve, duration));
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('vl-pdp-rider-stories', VLPDPRiderStories);
}
